import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import {
  Package,
  Search,
  MapPin,
  History,
  RefreshCw,
  Box,
  Hash,
} from "lucide-react";

import Pagination from "../../Pagination";
import Loader from "../../../loader/Loader";
import InventoryOverrideTable from "./InventoryOverrideTable";
import HistoryModal from "./HistoryModal";
import { domain, domainAccounting } from "../../../../security";
import { serviceCategories } from "../../Inventory/InventoryModule/AllInventory";
import { BrandSelect, CategorySelect } from "../../Selections";

const ITEMS_PER_PAGE = 10;

const AllInventoryOverride = () => {
  // --- States ---
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [availableLocations, setAvailableLocations] = useState([]);
  const [serialFilter, setSerialFilter] = useState("All");
  const [brand, setBrand] = useState(null);
  const [category, setCategory] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // History
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  // --- Fetch Data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    //const apiUrl = `${domain}/api/Products/physical-inventory-base`;

    const locationId = (!selectedLocation 
      || selectedLocation === "ALL" 
      || selectedLocation === "All") 
      ? 0 : parseInt(selectedLocation)
    console.log(typeof locationId, locationId, selectedLocation)
    
    try {
      //const response = await axios.get(apiUrl);
      const [response, locRes] =
                await Promise.all([
                  axios.get(`${domain}/api/Products/physical-inventory-base`),
                  // axios.get(`${domain}/api/Products/inventory-details/location/${locationId}`),
                  axios.get(`${domain}/api/Locations`),
                ]);

      const items = response?.data?.filter(i => 
                !serviceCategories.includes(i.categoryId)
                && i.isBom === false
              );

      setInventoryData(items);
      setAvailableLocations(locRes.data)

      /* const uniqueLocs = [
        ...new Set(
          response.data.map((item) => item.locationName).filter(Boolean)
        ),
      ];
      setAvailableLocations(uniqueLocs); */

      /* if (uniqueLocs.length > 0 && !selectedLocation) {
        setSelectedLocation(uniqueLocs[0]);
      } else if (uniqueLocs.length === 0) {
        setSelectedLocation("All");
      } */

      setLoading(false);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast.error("Failed to load inventory data");
      setLoading(false);
    }
  }, [selectedLocation]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- History Logic (UPDATED) ---
  // const fetchHistory = async () => {
  //   try {
  //     // Calls the new "Grouped" endpoint
  //     const res = await axios.get(`${domain}/api/Products/adjustment-batches`);
  //     setHistoryData(res.data);
  //     setShowHistory(true);
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Failed to fetch history batches.");
  //   }
  // };

  const fetchHistory = async () => {
    try {
      // Calls the new "Grouped" endpoint
      const res = await axios.get(`${domain}/api/Products/adjustment-logs`);
      setHistoryData(res.data);
      setShowHistory(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch history batches.");
    }
  };

  // --- NEW: Handle Batch Revert Logic ---
  // const handleRevert = async (batchItem) => {
  //   // 1. Safety Check / Confirmation
  //   const dateStr = new Date(batchItem.adjustmentDate).toLocaleString();
  //   const confirmMsg = `WARNING: You are about to revert the ENTIRE upload from ${dateStr}.\n\nThis will restore ${batchItem.itemCount} items to their previous state.\n\nType "CONFIRM" to proceed.`;

  //   const userInput = window.prompt(confirmMsg);

  //   if (userInput !== "CONFIRM") {
  //     if (userInput !== null) toast.info("Revert cancelled.");
  //     return;
  //   }

  //   // 2. Reason Input
  //   const reason = window.prompt("Please enter a reason for this rollback:");
  //   if (!reason || !reason.trim()) {
  //     toast.warning("Revert reason is required.");
  //     return;
  //   }

  //   try {
  //     // 3. Construct Payload for RevertBatchRequest DTO
  //     const payload = {
  //       BatchDate: batchItem.adjustmentDate,
  //       UserId: batchItem.userId,
  //       Reason: reason,
  //     };

  //     // 4. Call API
  //     await axios.post(`${domain}/api/Products/RevertBatch`, payload);

  //     toast.success("Checkpoint restored successfully!");

  //     // 5. Refresh Data
  //     fetchHistory(); // Refresh the list in the modal
  //     fetchData(); // Refresh the background table
  //   } catch (error) {
  //     console.error("Revert failed", error);
  //     const msg = error.response?.data?.message || "Failed to revert batch.";
  //     toast.error(msg);
  //   }
  // };

  const handleRevert = async (batchItem) => {
    // 1. Safety Check / Confirmation
    const dateStr = new Date(batchItem.adjustmentDate).toLocaleString();
    const confirmMsg = `WARNING: You are about to revert the ENTIRE upload from ${dateStr}.\n\nThis will restore ${batchItem.itemCount} items to their previous state.\n\nType "CONFIRM" to proceed.`;

    const userInput = window.prompt(confirmMsg);

    if (userInput !== "CONFIRM") {
      if (userInput !== null) toast.info("Revert cancelled.");
      return;
    }

    // 2. Reason Input
    const reason = window.prompt("Please enter a reason for this rollback:");
    if (!reason || !reason.trim()) {
      toast.warning("Revert reason is required.");
      return;
    }

    try {
      // 3. Construct Payload for RevertBatchRequest DTO
      const payload = {
        id: batchItem.id,
        reason: reason,
      };

      // 4. Call API
      await axios.post(`${domain}/api/Products/revert-adjustments`, payload);

      toast.success("Checkpoint restored successfully!");

      // 5. Refresh Data
      fetchHistory(); // Refresh the list in the modal
      fetchData(); // Refresh the background table
    } catch (error) {
      console.error("Revert failed", error);
      const msg = error.response?.data?.message || "Failed to revert batch.";
      toast.error(msg);
    }
  };

  // --- Filtering Logic (Unchanged) ---
  const filteredData = useMemo(() => {
    let data = inventoryData;

    if (selectedLocation && selectedLocation !== "All") {
      data = data.filter((item) => item.locationId?.toString() === selectedLocation?.toString());
    }

    if (serialFilter === "Serialized") {
      data = data.filter((item) => item.hasSerial === true);
    } else if (serialFilter === "Non-Serialized") {
      data = data.filter((item) => item.hasSerial === false);
    }

    if(category != null){
      data = data.filter((pricelist) => {
        return (
          pricelist.categoryId === category?.id
      )});
    }

    if(brand != null){
      data = data.filter((pricelist) => {
        return (
          pricelist.brandId === brand?.id
      )});
    }

    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      data = data.filter(
        (item) =>
          item.productName?.toLowerCase().includes(lowerQuery) ||
          item.itemCode?.toLowerCase().includes(lowerQuery) ||
          item.barCode?.toLowerCase().includes(lowerQuery)
      );
    }

    return data;
  }, [inventoryData, selectedLocation, searchQuery, serialFilter, brand, category]);

  // --- Pagination Logic (Unchanged) ---
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedLocation, serialFilter, brand, category]);

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const stats = useMemo(() => {
    const totalItems = filteredData.length;
    const totalGoodStock = filteredData.reduce(
      (acc, curr) => acc + (curr.unsoldCount || 0),
      0
    );
    return { totalItems, totalGoodStock };
  }, [filteredData]);

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const handleSyncToAccounting = async () => {
    if(inventoryData.length <= 0){
      toast.error(`No Orders to Sync.`);
      return
    }

    setLoading(true)
    try {
      const _data = inventoryData?.map((item)=> {
        return {
          itemCode: item.itemCode ?? "",
          locationId: item.locationId,
          totalStock: item.totalCount,
          goodStock: item.unsoldCount,
          soldStock: item.soldCount,
          badStock: item.badStock,
        }
      })

      await axios.post(`${domainAccounting}/api/ItemStock/bulk`, _data);
      toast.success(`Successfully pushed ${_data.length || 0} to Accounting!`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to push "order"} to Accounting.`);
    } finally {
      setLoading(false)
    }
  };

  return (
    <div className="min-h-screen pb-12 bg-slate-50/50">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Package className="text-indigo-600" /> Physical Inventory
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Overview of stock levels (Base Units) across locations.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openSyncToAccountingForm()}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Sync To Accounting</span>
              </button>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                <RefreshCw size={16} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={fetchHistory}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 text-indigo-700 font-medium rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <History size={18} /> View Checkpoints
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <Box size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Unique SKUs</p>
              <p className="text-xl font-bold text-slate-800">
                {stats.totalItems}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex items-center gap-4">
            <div className="p-3 bg-teal-50 text-teal-600 rounded-lg">
              <Package size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Good Stock</p>
              <p className="text-xl font-bold text-slate-800">
                {stats.totalGoodStock.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 grid md:grid-cols-12 gap-4 mb-6">
          <div className="relative md:col-span-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search product name, item code..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-gray-50"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="relative md:col-span-3">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 outline-none appearance-none bg-gray-50 cursor-pointer"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="All">All Locations</option>
              {availableLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.locationName}
                </option>
              ))}
            </select>
          </div>

          <div className="relative md:col-span-3">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 outline-none appearance-none bg-gray-50 cursor-pointer"
              value={serialFilter}
              onChange={(e) => setSerialFilter(e.target.value)}
            >
              <option value="All">All Serials</option>
              <option value="Serialized">Has Serial</option>
              <option value="Non-Serialized">No Serial</option>
            </select>
          </div>

          <div className="relative md:col-span-4">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">
              Brand
            </label>
            <BrandSelect data={brand} setData={setBrand} isEditable={true} />
          </div>
  
          <div className="relative md:col-span-4">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 ml-1">
              Category
            </label>
            <CategorySelect data={category} setData={setCategory} isEditable={true} />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            <InventoryOverrideTable
              data={currentItems}
              allData={filteredData}
              serialFilter={serialFilter}
              onRefresh={fetchData}
            />

            <div className="mt-6 mb-10">
              <Pagination
                itemsPerPage={ITEMS_PER_PAGE}
                totalItems={filteredData.length}
                currentPage={currentPage}
                paginate={paginate}
              />
            </div>
          </>
        )}
      </div>

      {/* --- Modals --- */}
      <HistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        historyData={historyData}
        onRevert={handleRevert}
      />

      <SyncToAccountingModal 
        isOpen={showSyncToAccountingForm}
        onClose={closeSyncToAccountingForm}
        onConfirm={handleSyncToAccounting}
      />
    </div>
  );
};

const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
          </p>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllInventoryOverride;
